/**
 * FamilyPage Component
 * Family management page for parent mode
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, UserPlus, Trash2, Loader2, ArrowLeft } from 'lucide-react';
import { familyApi, FamilyOverview } from '../../services/familyApi';
import { useBasePath } from '../../contexts/BasePathContext';
import { buildStaticUrl } from '../../config/env';

export const FamilyPage: React.FC = () => {
  const navigate = useNavigate();
  const { basePath } = useBasePath();
  const [overview, setOverview] = useState<FamilyOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [removingId, setRemovingId] = useState<string | null>(null);

  const fetchOverview = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await familyApi.getOverview();
      setOverview(data);
    } catch (err: any) {
      setError(err.message || '获取家庭信息失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  const handleRemoveChild = async (childId: string, nickname: string) => {
    if (!confirm(`确定要移除家庭成员「${nickname}」吗？移除后该成员的会员权益将被重置。`)) return;
    try {
      setRemovingId(childId);
      await familyApi.removeChild(childId);
      await fetchOverview();
    } catch (err: any) {
      setError(err.message || '移除失败');
    } finally {
      setRemovingId(null);
    }
  };

  const getMembershipText = (type: string) => {
    switch (type) {
      case 'monthly': return '月度会员';
      case 'yearly': return '年度会员';
      default: return '免费用户';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center space-x-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">家庭成员</h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {overview && (
          <>
            {/* Membership Info Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">家庭套餐</h2>
                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-sm">
                  {getMembershipText(overview.membershipType)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500 dark:text-gray-400">到期时间</span>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {overview.membershipExpiresAt
                      ? new Date(overview.membershipExpiresAt).toLocaleDateString('zh-CN')
                      : '未订阅'}
                  </p>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-gray-400">成员名额</span>
                  <p className="text-gray-900 dark:text-white font-medium">
                    {overview.childrenCount} / {overview.maxMembers - 1} 名子女
                  </p>
                </div>
              </div>
            </div>

            {/* Children List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-gray-500" />
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">家庭成员</h2>
                </div>
                {overview.childrenCount < overview.maxMembers - 1 && (
                  <button
                    onClick={() => navigate(`${basePath}/family/add-child`)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    添加家庭成员
                  </button>
                )}
              </div>

              {overview.children.length === 0 ? (
                <div className="p-8 text-center">
                  <Users className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400 mb-4">还没有添加家庭成员</p>
                  <button
                    onClick={() => navigate(`${basePath}/family/add-child`)}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    添加家庭成员
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-200 dark:divide-gray-700">
                  {overview.children.map((child) => (
                    <div key={child.id} className="flex items-center justify-between p-4">
                      <div className="flex items-center space-x-3">
                        {child.avatar_url ? (
                          <img src={buildStaticUrl(child.avatar_url)} alt="" className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center text-white font-semibold">
                            {child.nickname?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div>
                          <p className="text-gray-900 dark:text-white font-medium">{child.nickname}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {getMembershipText(child.membershipType)}
                            {child.lastActivityAt && ` · 最近活跃 ${new Date(child.lastActivityAt).toLocaleDateString('zh-CN')}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveChild(child.id, child.nickname)}
                        disabled={removingId === child.id}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                        title="移除成员"
                      >
                        {removingId === child.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
